import React, { useRef, useState, useEffect, useCallback } from "react";
import CupPathCard from "./CupPathCard";

// Club Matches Tab — standings strip (handoff MatchesTabContent.dc.html,
// tokens transcribed verbatim; brief §0.3):
//   * club context, MATCH-INDEPENDENT — parent must never re-render it on
//     rail selection changes (it takes no selection prop by design),
//   * points cards: excerpt grid `# · Team · Sp · Diff · Pkt`
//     (18px/1fr/26px/34px/30px), club row highlighted, team rows click,
//   * KO competitions render the cup-path card (own component),
//   * horizontal scroll on overflow with fade + chevron affordance —
//     NEVER wraps, never pushes the detail area down.
//
// props.cards: [{ key, name, logo, isPoints, rows } | { key, name, logo,
// isKo, cupPath }] — names arrive ALREADY resolved through the
// name-override resolver (short_name in tight spots, brief §0.1).
// props.stack: mobile stack mode (C11) — 268px cards, swipe.
// props.onTeamClick(teamId): navigation callback for team rows.
// props.onCompetitionClick(key): optional — makes the card header a click
// target (full table / cup lives on the competition page; the card itself
// stays the compact excerpt, brief §0.3).

const MonoChip = ({ text, logo, size = 20, blue = false }) => (
  <span
    style={{
      width: size,
      height: size,
      flex: `0 0 ${size}px`,
      borderRadius: 4,
      background: blue ? "#EAF3FF" : "#EEF2F7",
      border: `1px solid ${blue ? "#D3E6FF" : "#E2E8F0"}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: size >= 20 ? 8 : 7,
      fontWeight: 700,
      color: blue ? "#2183FE" : "#475569",
      overflow: "hidden",
    }}
  >
    {logo ? (
      <img
        src={logo}
        alt=""
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    ) : (
      text || null
    )}
  </span>
);

const mono = (name) =>
  (name || "")
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const PointsRows = ({ rows, onTeamClick }) => (
  <div style={{ paddingTop: 8 }}>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "18px 1fr 26px 34px 30px",
        gap: 4,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: ".04em",
        color: "#94A3B8",
        padding: "0 2px 4px 2px",
        borderBottom: "1px solid #EDF1F6",
      }}
    >
      <span>#</span>
      <span>Team</span>
      <span style={{ textAlign: "right" }}>Pld</span>
      <span style={{ textAlign: "right" }}>GD</span>
      <span style={{ textAlign: "right" }}>Pts</span>
    </div>
    {rows.map((r) => (
      <div
        key={`${r.pos}-${r.teamId || r.name}`}
        onClick={r.teamId && onTeamClick ? () => onTeamClick(r.teamId) : undefined}
        role={r.teamId ? "button" : undefined}
        style={{
          display: "grid",
          gridTemplateColumns: "18px 1fr 26px 34px 30px",
          gap: 4,
          alignItems: "center",
          fontSize: 12,
          padding: "5px 2px",
          borderRadius: 4,
          cursor: r.teamId ? "pointer" : "default",
          background: r.isClub ? "#EAF3FF" : "transparent",
          color: r.isClub ? "#2183FE" : "#334155",
          fontWeight: r.isClub ? 700 : 500,
        }}
      >
        <span style={{ color: r.isClub ? "#2183FE" : "#94A3B8" }}>{r.pos}</span>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            minWidth: 0,
          }}
        >
          <MonoChip text={mono(r.name)} logo={r.logo} size={16} />
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {r.name}
          </span>
        </span>
        <span style={{ textAlign: "right" }}>{r.pld}</span>
        <span style={{ textAlign: "right" }}>{r.gd}</span>
        <span style={{ textAlign: "right", fontWeight: 700 }}>{r.pts}</span>
      </div>
    ))}
  </div>
);

const StandingsStrip = ({
  cards,
  stack = false,
  wide = false,
  onTeamClick,
  onCompetitionClick,
}) => {
  const list = Array.isArray(cards) ? cards.filter(Boolean) : [];
  const scrollRef = useRef(null);
  const [overflow, setOverflow] = useState(false);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setOverflow(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, list.length]);

  if (!list.length) return null; // empty renders nothing (brief §2)

  const cardW = stack ? 268 : wide ? 340 : 300;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "0 0 10px 0",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: ".09em",
            textTransform: "uppercase",
            color: "#2183FE",
          }}
        >
          Standings
        </span>
        <span style={{ height: 1, flex: 1, background: "#EDF1F6" }} />
      </div>
      <div style={{ position: "relative" }}>
        <div
          ref={scrollRef}
          onScroll={measure}
          style={{
            display: "flex",
            gap: 12,
            overflowX: "auto",
            paddingBottom: overflow || stack ? 8 : 2,
            scrollbarWidth: "thin",
          }}
        >
          {list.map((c) => (
            <div
              key={c.key || c.name}
              style={{
                flex: `0 0 ${cardW}px`,
                width: cardW,
                border: "1px solid #E2E8F0",
                borderRadius: 8,
                background: "#FFFFFF",
                padding: "10px 12px",
              }}
            >
              <div
                onClick={
                  onCompetitionClick ? () => onCompetitionClick(c.key) : undefined
                }
                role={onCompetitionClick ? "button" : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  minWidth: 0,
                  cursor: onCompetitionClick ? "pointer" : undefined,
                }}
              >
                <MonoChip text={mono(c.name)} logo={c.logo} blue />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#334155",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {c.name}
                </span>
              </div>
              {c.isKo && c.cupPath ? (
                <CupPathCard cupPath={c.cupPath} />
              ) : null}
              {c.isPoints && Array.isArray(c.rows) && c.rows.length ? (
                <PointsRows rows={c.rows} onTeamClick={onTeamClick} />
              ) : null}
            </div>
          ))}
        </div>
        {overflow && (
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: 64,
              background:
                "linear-gradient(to right,rgba(255,255,255,0),#FFFFFF 62%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              pointerEvents: "none",
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="8"
                height="12"
                viewBox="0 0 8 12"
                fill="none"
                stroke="#475569"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="2,1 6,6 2,11"></polyline>
              </svg>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StandingsStrip;
